<template>
  <div class="location-autocomplete">
    <v-combobox
      ref="combobox"
      :value="dirtyValue"
      :items="combinedLocationItems"
      :loading="loading || loadingPlaces"
      :search-input.sync="locationSearch"
      :filter="customFilter"
      item-text="name"
      item-value="id"
      return-object
      clearable
      v-bind="$attrs"
      @input="onLocationSelected"
    >
      <template v-slot:item="{ item }">
        <div
          class="d-flex align-center justify-space-between"
          style="width: 100%;"
        >
          <div
            class="text-truncate flex-grow-1"
            style="max-width: 450px; min-width: 0;"
          >
            {{ item.name }}
          </div>

          <div class="d-flex flex-shrink-0">
            <v-icon
              v-if="item.isSaved === true"
              small
            >
              far fa-check-circle
            </v-icon>
            <v-icon
              v-else-if="item.isSaved === false && item.place_id"
              small
            >
              fab fa-google
            </v-icon>
          </div>
        </div>
      </template>
      <template v-slot:selection="{ item }">
        <span
          v-if="item"
          style="max-width: 450px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis"
        >
          {{ item.name }}{{ item.formatted_address ? ', ' + item.formatted_address : '' }}
        </span>
      </template>
    </v-combobox>
  </div>
</template>

<script>
import { GooglePlacesApiService } from '@/data/services/GooglePlacesApiService'

export default {
  name: 'LocationAutocomplete',
  props: {
    value: {
      type: Object,
      default: null
    },
    locations: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      locationSearch: '',
      googleSuggestions: [],
      loadingPlaces: false,
      debounceTimer: null
    }
  },
  computed: {
    dirtyValue: {
      get() {
        return this.value
      },
      set(newValue) {
        this.$emit('input', newValue)
      }
    },
    savedLocations() {
      return (this.locations || []).map((loc) => ({
        ...loc,
        isSaved: true
      }))
    },
    combinedLocationItems() {
      return [
        ...this.savedLocations,
        ...this.googleSuggestions
      ]
    }
  },
  watch: {
    locationSearch(newValue) {
      this.onLocationSearchInput(newValue)
    }
  },
  methods: {
    onLocationSearchInput(value) {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer)
      }

      if (!value || value.trim().length === 0) {
        this.googleSuggestions = []
        return
      }

      const exactMatch = this.savedLocations.some(
          (loc) => loc.name.toLowerCase() === value.toLowerCase()
      )

      if (exactMatch) {
        this.googleSuggestions = []
        return
      }

      this.debounceTimer = setTimeout(() => {
        this.searchGooglePlaces(value)
      }, 300)
    },
    async searchGooglePlaces(query) {
      try {
        this.loadingPlaces = true
        const response = await GooglePlacesApiService.getPredictions(query)

        if (!response.places || response.places.length === 0) {
          this.googleSuggestions = []
          return
        }

        this.googleSuggestions = response.places.map(place => {
          return {
            place_id: place.id,
            name: place.displayName.text + ', ' + place.formattedAddress,
            isSaved: false
          }
        })
      } catch (error) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: error.message || 'Failed to search locations.',
          color: 'error'
        })
        this.googleSuggestions = []
      } finally {
        this.loadingPlaces = false
      }
    },
    async onLocationSelected(selection) {
      if (!selection) {
        this.$emit('input', null)
        return
      }

      if (typeof selection === 'string') {
        const trimmedText = selection.trim()
        if (trimmedText) {
          this.$emit('input', {
            name: trimmedText,
            isSaved: false,
            isNew: true,
            fromGoogle: false
          })
        }
        this.locationSearch = ''
        return
      }

      if (selection.isSaved === true && selection.id) {
        this.$emit('input', selection)
        this.locationSearch = ''
        return
      }

      if (selection.place_id && !selection.id) {
        try {
          this.loadingPlaces = true
          const placeData = await GooglePlacesApiService.getPlaceDetails(
              selection.place_id
          )

          this.$emit('input', {
            place_id: placeData.id || selection.place_id,
            name: placeData.displayName?.text || placeData.name || '',
            formatted_address: placeData.formattedAddress || '',
            geometry: {
              location: {
                lat: placeData.location?.latitude,
                lng: placeData.location?.longitude
              }
            },
            isSaved: false,
            isNew: true,
            fromGoogle: true
          })
          this.locationSearch = ''
        } catch (error) {
          this.$store.dispatch(this.$storeActions.ALERT, {
            text: error.message || 'Failed to load location details.',
            color: 'error'
          })
        } finally {
          this.loadingPlaces = false
        }
        return
      }

      if (selection && selection.name && !selection.id && !selection.place_id) {
        this.$emit('input', {
          name: selection.name.trim(),
          isSaved: false,
          isNew: true,
          fromGoogle: false
        })
        this.locationSearch = ''
      }
    },
    customFilter(item, queryText, itemText) {
      if (item.isSaved === false && item.place_id) {
        return true
      }

      const searchText = queryText.toLowerCase()
      const text = itemText.toLowerCase()
      return text.indexOf(searchText) > -1
    }
  }
}
</script>

<style scoped>
</style>
